import { gql } from "generated-graphql";
import { MultipleFacilityPickerFragment } from "generated-graphql/graphql";
import React, { forwardRef, useCallback } from "react";
import { FieldError } from "react-hook-form";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { ErrorDisplay } from "./Forms/ErrorDisplay";
import { SxProps } from "@mui/material";
import {
  useMultiPickerWithHydration,
  PickerInput,
} from "../hooks/useMultiPickerWithHydration";

gql(`
  fragment MultipleFacilityPicker on Facility {
    id
    name
  }
`);

const FACILITIES_QUERY = gql(`
  query PickerFacilitiesQuery($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...MultipleFacilityPicker
      }
      count
    }
  }
`);

interface MultipleFacilityPickerProps {
  onChange: (facilities: MultipleFacilityPickerFragment[] | null) => void;
  defaultSearchTerm?: string;
  disabledFacilitiesById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?:
    | (
        | MultipleFacilityPickerFragment
        | PickerInput<MultipleFacilityPickerFragment>
      )[]
    | null
    | undefined;
  disabled?: boolean;
  showErrors?: boolean;
  error?: FieldError;
  sx?: SxProps;
}

export const MultipleFacilityPicker: React.FC<MultipleFacilityPickerProps> =
  forwardRef<HTMLDivElement, MultipleFacilityPickerProps>(
    function MultipleFacilityPicker(
      {
        onChange,
        defaultSearchTerm = "",
        disabledFacilitiesById = [],
        disabledOptionLabel = "",
        label = "Facility",
        value,
        error,
        disabled,
        showErrors = false,
        sx,
      },
      ref
    ) {
      const getItems = useCallback(
        (data: any) => data.facilities?.items ?? [],
        []
      );

      const { resolvedValue, isHydrating, enhancedOnChange, enhancedGetItems } =
        useMultiPickerWithHydration<MultipleFacilityPickerFragment, any>({
          value,
          query: FACILITIES_QUERY,
          getItems,
          idsSearchPrefix: "ids",
          onChange,
        });

      return (
        <>
          <AsyncAutocomplete
            ref={ref}
            query={FACILITIES_QUERY}
            initialPageSize={20}
            variables={{}}
            getItems={enhancedGetItems}
            getOptionLabel={(facility) => facility.name ?? ""}
            placeholderLabel={label}
            onChange={enhancedOnChange}
            value={resolvedValue}
            error={error}
            disabled={disabled || isHydrating}
            additionalSearchString={defaultSearchTerm}
            multiple={true}
            filterSelectedOptions
            getOptionDisabled={(option: MultipleFacilityPickerFragment) =>
              disabledFacilitiesById.includes(option?.id ?? "")
            }
            loading={isHydrating}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
                {disabledFacilitiesById.includes(option?.id ?? "") && (
                  <span>&nbsp;({disabledOptionLabel})</span>
                )}
              </li>
            )}
            sx={sx}
          />
          {showErrors && <ErrorDisplay error={error} />}
        </>
      );
    }
  );
